<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
      <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-2">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-bold uppercase tracking-wider">Voltar</span>
          </button>
          <h1 class="text-2xl font-bold text-slate-700">{{ id ? 'Editar Usuário' : 'Cadastrar Usuário' }}</h1>
        </div>
      </div>

      <div class="space-y-6 pb-12">
        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="space-y-6">
            <!-- SEÇÃO 1: IDENTIFICAÇÃO -->
            <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-account-circle-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E ACESSO</h2>
              </div>
              
              <div class="p-8">
                <div class="flex flex-col md:flex-row gap-10 items-center md:items-start">
                  <!-- PHOTO UPLOAD -->
                  <div class="flex flex-col items-center gap-4">
                    <div 
                      class="relative group cursor-pointer"
                      @click="$refs.fileInput.click()"
                    >
                      <v-avatar size="160" class="border-4 border-slate-50 shadow-md transition-transform group-hover:scale-[1.02] bg-slate-100">
                        <img
                          :src="data.user_photo ? $store.state.urlImages + data.user_photo : 'https://placehold.co/400'"
                          class="object-cover"
                        />
                      </v-avatar>
                      <div class="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <v-icon color="white">mdi-camera-plus</v-icon>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg, image/png"
                      @change="uploadImage"
                      ref="fileInput"
                      style="display: none"
                    />
                    <p class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Clique para alterar a foto</p>
                  </div>

                  <!-- BASIC FIELDS -->
                  <div class="flex-1 w-full space-y-6 pt-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Login / Usuário</label>
                        <input 
                          type="text" 
                          v-model="data.user_login"
                          placeholder="EX: JOAO.SILVA"
                          class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                        >
                      </div>
                      
                      <div>
                        <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Senha</label>
                        <input 
                          type="password" 
                          v-model="data.user_password"
                          placeholder="********"
                          class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                        >
                      </div>

                      <div class="md:col-span-2">
                        <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Grupo de Acesso</label>
                        <select
                          v-model="data.group_id"
                          @change="getTable(data.group_id)"
                          class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                        >
                          <option :value="null" disabled>SELECIONE O GRUPO</option>
                          <option v-for="item in groups" :key="item.group_id" :value="item.group_id">
                            {{ item.group_name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SEÇÃO 2: VÍNCULO COM ENTIDADE -->
            <div class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-link-variant</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VÍNCULO COM ENTIDADE</h2>
              </div>
              
              <div class="p-8">
                <div class="grid grid-cols-1 gap-6">
                  <div>
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Vincular a:</label>
                    <select
                      v-model="data.table_id"
                      :disabled="!table || table.length === 0"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase disabled:opacity-50"
                    >
                      <option :value="null">SELECIONE UMA OPÇÃO</option>
                      <option v-for="item in table" :key="getEntityId(item)" :value="getEntityId(item)">
                        {{ getEntityName(item) }}
                      </option>
                    </select>
                    <p v-if="!table || table.length === 0" class="text-[10px] text-slate-400 mt-2 italic">
                      * Selecione um grupo de acesso primeiro para carregar as opções de vínculo.
                    </p>
                  </div>
                </div>
              </div>

              <!-- FOOTER INTEGRADO -->
              <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
                <button 
                  type="button"
                  @click="$router.back()"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    type="button"
                    @click="validate"
                    :disabled="loader || !valid"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </v-form>
      </div>

      <!-- NOTIFICAÇÕES -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="3000"
        rounded="lg"
        elevation="24"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white" small>
            {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
 </v-container>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    groups: [],
    table: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      user_login: "",
      user_password: null,
      user_photo: "",
      group_id: null,
      table_id: null,
      table_key: "",
    },
  }),
  created() {
    this.$http.get("/access-groups").then((r) => (this.groups = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`users/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.getTable(this.data.group_id);
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    getEntityId(item) {
      if (this.data.table_key === 'providers') return item.provider_id;
      if (this.data.table_key === 'prefectures') return item.prefecture_id;
      if (this.data.table_key === 'drivers') return item.driver_id;
      if (this.data.table_key === 'attendants') return item.attendant_id;
      return '';
    },
    getEntityName(item) {
      if (this.data.table_key === 'providers') return item.provider_name;
      if (this.data.table_key === 'prefectures') return item.prefecture_social_name;
      if (this.data.table_key === 'drivers') return item.driver_name;
      if (this.data.table_key === 'attendants') return item.attendant_name;
      return '';
    },
    getTable(gid) {
      const tb = this.groups.filter((v) => v.group_id == gid && v);
      this.table = [];
      setTimeout(() => {
        const modules = this.$store.getters.getModules;
        const vl = modules.filter(
          (nv) => nv.nome.toLowerCase() == tb[0].group_name.toLowerCase() && nv
        );
        if (vl && vl.length > 0) {
          this.data.table_key = vl[0].name;
          this.$http(`/${vl[0].name}`)
            .then((r) => (this.table = r.data))
            .catch(() => (this.table = []));
        } else {
          this.table = [];
        }
      }, 400);
    },
    validate() {
      if (!this.data.user_login || !this.data.group_id) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: O login e o grupo são campos obrigatórios!";
        return;
      }
      this.createOrUpdate();
    },
    createOrUpdate() {
      this.loader = true;
      const endpoint = this.id ? `/users/${this.id}` : "/users";
      const method = this.id ? "put" : "post";
      
      this.$store.dispatch("setLoader", true);
      this.$http[method](endpoint, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          setTimeout(() => {
            this.$router.push("/users-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http
        .post("/upload", fd)
        .then((r) => {
          this.data.user_photo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch((e) => {
          alert("Erro ao realizar upload da imagem tente novamente! " + e.message);
          this.$store.dispatch("setLoader", false);
          event.target.files = [];
        });
    },
  },
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.border-slate-200 { border-color: #e2e8f0; }
.text-slate-400 { color: #94a3b8; }
.text-slate-500 { color: #64748b; }
.text-slate-700 { color: #334155; }
</style>